import {
  broadcastChannelFactory,
  buildFeedbackContext,
  createPromptController,
  defaultTriggerStore
} from "./chunk-2AWG4AQT.js";

// src/react/useFeedbackGate.ts
import { useEffect, useMemo, useRef, useSyncExternalStore } from "react";

// src/core/gateModel.ts
var NONE = { kind: "none" };
function createGateModel({
  controller,
  triggerStore
}) {
  const listeners = /* @__PURE__ */ new Set();
  let suppressed = false;
  let unsubscribe = null;
  let lastPrompt = null;
  let lastTrigger = null;
  let lastSuppressed = false;
  const submit = (values) => controller.submit(values);
  const skip = () => controller.skip();
  const continueAnyway = () => controller.continueAnyway();
  const submitAdhoc = (values) => controller.submitAdhoc(values);
  const closeAdhoc = () => {
    triggerStore.close();
    const { submission } = controller.getSnapshot();
    if (submission.status === "error" && submission.kind === "adhoc") controller.resetSubmission();
  };
  const dismiss = () => {
    controller.resetSubmission();
    triggerStore.close();
  };
  const failureOf = (prompt, adhoc) => {
    const { submission } = prompt;
    return submission.status === "error" && submission.kind === "adhoc" === adhoc ? submission : null;
  };
  const viewOf = (prompt, trigger) => {
    const { submission } = prompt;
    if (!prompt.enabled) return NONE;
    if (submission.status === "thanked") {
      return { kind: "thanks", about: submission.kind, outcome: submission.outcome, dismiss };
    }
    if (suppressed) return NONE;
    const sending = submission.status === "sending";
    if (prompt.periodic) {
      const { form, mode, cycleId, canContinue } = prompt.periodic;
      return {
        kind: "periodic",
        form,
        cycleId,
        canSkip: mode === "optional",
        canContinue,
        sending,
        failure: failureOf(prompt, false),
        submit,
        skip,
        continueAnyway
      };
    }
    if (trigger.isOpen && prompt.adhoc) {
      return { kind: "adhoc", form: prompt.adhoc, sending, failure: failureOf(prompt, true), submit: submitAdhoc, close: closeAdhoc };
    }
    return NONE;
  };
  let snapshot = {
    view: viewOf(controller.getSnapshot(), triggerStore.getSnapshot()),
    available: false,
    prompt: controller.getSnapshot()
  };
  let syncing = false;
  let again = false;
  const sync = () => {
    if (syncing) {
      again = true;
      return;
    }
    syncing = true;
    let changed = false;
    try {
      do {
        again = false;
        changed = step() || changed;
      } while (again);
    } finally {
      syncing = false;
    }
    if (changed) listeners.forEach((listener) => listener());
  };
  const step = () => {
    const prompt = controller.getSnapshot();
    const dueArrived = Boolean(prompt.due && !(lastPrompt == null ? void 0 : lastPrompt.due));
    const reopened = triggerStore.getSnapshot().isOpen && !(lastTrigger == null ? void 0 : lastTrigger.isOpen);
    if (dueArrived) triggerStore.close();
    if ((dueArrived || reopened) && prompt.submission.status === "error" && prompt.submission.kind === "adhoc") {
      controller.resetSubmission();
    }
    triggerStore.setAvailable(listeners.size > 0 && prompt.enabled && prompt.adhoc !== null && !suppressed);
    const settledPrompt = controller.getSnapshot();
    const trigger = triggerStore.getSnapshot();
    if (settledPrompt === lastPrompt && trigger === lastTrigger && lastSuppressed === suppressed) return false;
    lastPrompt = settledPrompt;
    lastTrigger = trigger;
    lastSuppressed = suppressed;
    snapshot = { view: viewOf(settledPrompt, trigger), available: trigger.available, prompt: settledPrompt };
    return true;
  };
  return {
    getSnapshot: () => snapshot,
    subscribe(listener) {
      listeners.add(listener);
      if (listeners.size === 1) {
        const fromController = controller.subscribe(sync);
        const fromStore = triggerStore.subscribe(sync);
        unsubscribe = () => {
          fromController();
          fromStore();
        };
        sync();
      }
      return () => {
        if (!listeners.delete(listener) || listeners.size > 0) return;
        unsubscribe == null ? void 0 : unsubscribe();
        unsubscribe = null;
        lastPrompt = null;
        lastTrigger = null;
        triggerStore.setAvailable(false);
      };
    },
    open: () => triggerStore.open(),
    suppress() {
      if (suppressed) return;
      suppressed = true;
      sync();
    }
  };
}

// src/react/useFeedbackGate.ts
function useFeedbackGate({ triggerStore, ...options }) {
  const { enabled = true, locationKey = "", pollMs, timeoutMs, channelName, stuckAfter } = options;
  const latest = useRef(options);
  latest.current = options;
  const controller = useMemo(
    () => createPromptController({
      client: {
        getPrompt: (request) => latest.current.client.getPrompt(request),
        submit: (submission, request) => latest.current.client.submit(submission, request)
      },
      enabled,
      locationKey,
      pollMs,
      timeoutMs,
      channelName,
      stuckAfter,
      createChannel: (name, onMessage) => {
        var _a;
        return ((_a = latest.current.createChannel) != null ? _a : broadcastChannelFactory)(name, onMessage);
      },
      window: options.window,
      document: options.document,
      context: () => {
        var _a;
        return ((_a = latest.current.context) != null ? _a : buildFeedbackContext)();
      },
      onError: (error, phase) => {
        var _a, _b;
        return (_b = (_a = latest.current).onError) == null ? void 0 : _b.call(_a, error, phase);
      }
    }),
    // `enabled` and `locationKey` are applied below; rebuilding for them would drop the state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pollMs, timeoutMs, channelName, stuckAfter]
  );
  useEffect(() => controller.setEnabled(enabled), [controller, enabled]);
  useEffect(() => controller.setLocationKey(locationKey), [controller, locationKey]);
  const store = useMemo(() => triggerStore != null ? triggerStore : defaultTriggerStore(), [triggerStore]);
  const model = useMemo(() => createGateModel({ controller, triggerStore: store }), [controller, store]);
  const snapshot = useSyncExternalStore(model.subscribe, model.getSnapshot, model.getSnapshot);
  return useMemo(() => ({ ...snapshot, suppress: model.suppress }), [snapshot, model]);
}

// src/react/useFeedbackTrigger.ts
import { useSyncExternalStore as useSyncExternalStore2 } from "react";
function useFeedbackTrigger(store = defaultTriggerStore()) {
  const { available, isOpen } = useSyncExternalStore2(store.subscribe, store.getSnapshot, store.getSnapshot);
  return { open: store.open, available, isOpen };
}

export {
  useFeedbackGate,
  useFeedbackTrigger
};
